import {
  ApiDelete,
  ApiGet,
  ApiPatch,
  ApiPost,
} from "../../../helpers/API/ApiData";

const ADMIN_MEDIA = "admin/media";

const responseBody = (response) => response?.data || response || {};

const statusGuidance = {
  400: "Check the submitted values and try again.",
  401: "Your session has expired. Sign in again.",
  403: "Your account does not have permission to manage media.",
  404: "The media record could not be found.",
  409: "The media record changed or is not eligible for this action.",
  410: "The upload authorization expired. Start a new upload.",
  413: "The file exceeds the configured upload limit.",
  422: "The file format or media properties failed verification.",
  429: "Too many upload requests. Wait briefly and try again.",
  500: "The server could not complete the media request.",
  502: "Storage verification failed. Retry or contact an administrator.",
  503: "Media storage or processing is temporarily unavailable.",
};

const codeGuidance = {
  ASSET_PROCESSING: "We're preparing your video. This may take a few minutes.",
  ASSET_VERIFYING: (payload) => {
    const seconds = Number(payload?.retryAfterSeconds);
    return Number.isFinite(seconds) && seconds > 0
      ? `Verification is still running. Refresh status, or retry in about ${Math.ceil(seconds)} seconds.`
      : "Verification is still running. Refresh status before retrying.";
  },
  PUBLISHED_ASSET_REQUIRED: "Published media must keep a playable asset. Use Replace instead.",
  UPLOAD_SUPERSEDED: "A newer file was selected. The earlier upload was safely ignored.",
  UPLOAD_ALREADY_COMPLETED: "The upload completed before cancellation. The latest server state was restored.",
};

export class MediaApiError extends Error {
  constructor(message, { status, code, readinessErrors, payload } = {}) {
    super(message);
    this.name = "MediaApiError";
    this.status = status;
    this.code = code;
    this.readinessErrors = readinessErrors || [];
    this.payload = payload || {};
  }
}

export const toMediaApiError = (error, fallback = "The media request failed.") => {
  if (error instanceof MediaApiError) return error;
  const body = error?.response ? responseBody(error.response) : responseBody(error);
  const status = Number(body.status || error?.response?.status || error?.status) || undefined;
  const payload = body.payload || {};
  const detail = Array.isArray(payload.readinessErrors) && payload.readinessErrors.length
    ? payload.readinessErrors.join("; ")
    : "";
  const base = body.message || error?.message || fallback;
  const codeHelp = codeGuidance[payload.code];
  const guidance = detail
    ? ""
    : (typeof codeHelp === "function" ? codeHelp(payload) : codeHelp) || statusGuidance[status];
  return new MediaApiError(
    [base, detail, guidance && guidance !== base ? guidance : ""].filter(Boolean).join(" "),
    { status, code: payload.code, readinessErrors: payload.readinessErrors, payload }
  );
};

const unwrap = (response) => {
  const body = responseBody(response);
  if (body.result !== 0) throw toMediaApiError(body);
  return body.payload || {};
};

const call = async (request, fallback) => {
  try {
    return unwrap(await request());
  } catch (error) {
    throw toMediaApiError(error, fallback);
  }
};

const queryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) query.set(key, value);
  });
  return query.toString();
};

export const mediaApi = {
  list: (params) =>
    call(() => ApiGet(`${ADMIN_MEDIA}?${queryString(params)}`), "Media could not be loaded."),
  create: (data) =>
    call(() => ApiPost(ADMIN_MEDIA, data), "The media draft could not be created."),
  get: (id) =>
    call(() => ApiGet(`${ADMIN_MEDIA}/${id}`), "The media record could not be loaded."),
  update: (id, data) =>
    call(() => ApiPatch(`${ADMIN_MEDIA}/${id}`, data), "The media draft could not be saved."),
  publish: (id) =>
    call(() => ApiPost(`${ADMIN_MEDIA}/${id}/publish`, {}), "The media could not be published."),
  unpublish: (id) =>
    call(() => ApiPost(`${ADMIN_MEDIA}/${id}/unpublish`, {}), "The media could not be unpublished."),
  archive: (id) =>
    call(() => ApiPost(`${ADMIN_MEDIA}/${id}/archive`, {}), "The media could not be archived."),
  remove: (id) =>
    call(() => ApiDelete(`${ADMIN_MEDIA}/${id}`), "The media draft could not be deleted."),
  removeAsset: (id, assetType) =>
    call(
      () => ApiDelete(`${ADMIN_MEDIA}/${id}/assets/${encodeURIComponent(assetType)}`),
      `The ${assetType} could not be removed.`
    ),
  uploadIntent: (id, data) =>
    call(() => ApiPost(`${ADMIN_MEDIA}/${id}/upload-intent`, data), "An upload could not be authorized."),
  uploadComplete: (id, uploadSessionId) =>
    call(
      () => ApiPost(`${ADMIN_MEDIA}/${id}/upload-complete`, { uploadSessionId }),
      "The upload could not be verified."
    ),
  uploadCancel: (id, uploadSessionId) =>
    call(
      () => ApiPost(`${ADMIN_MEDIA}/${id}/upload-cancel`, { uploadSessionId }),
      "The upload could not be cancelled safely."
    ),
  retryProcessing: (id) =>
    call(
      () => ApiPost(`${ADMIN_MEDIA}/${id}/process-retry`, {}),
      "Video processing could not be retried."
    ),
  preview: (id, assetType) =>
    call(
      () => ApiGet(`${ADMIN_MEDIA}/${id}/preview?assetType=${encodeURIComponent(assetType)}`),
      "The secure media preview could not be loaded."
    ),
  regeneratePoster: (id, focalPoint) =>
    call(
      () => ApiPost(`${ADMIN_MEDIA}/${id}/poster-regenerate`, { focalPoint }),
      "The Short poster could not be regenerated."
    ),
};

export { queryString, unwrap };
